/*───────────────────────────────────────────────────────────────
 Project: Ring2All Platform
 Author: Rodrigo Cuadra
 Company: Ring2All LLC
 File: DashboardTab.tsx
 Description:
     Dashboard tab component for Appearance settings.
     Mirrors Kalixor SBC architecture: Widget Columns, Auto-Refresh,
     per-theme color pickers with SimpleTooltip labels.

 IMPORTANT: WidgetColorPicker is defined at MODULE level (not inside DashboardTab).
 If defined inside, React creates a new component type on every re-render,
 causing unmount+remount which immediately closes the native color picker.
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { LayoutGrid } from 'lucide-react';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import type { AppearanceForm, WidgetPrefs } from '../types';
import { DEFAULT_WIDGET_DARK, DEFAULT_WIDGET_LIGHT } from '../types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const SHADOW_MAP: Record<string, string> = {
  none:   'none',
  light:  '0 1px 3px rgba(0,0,0,0.1)',
  medium: '0 4px 6px rgba(0,0,0,0.15)',
  heavy:  '0 10px 25px rgba(0,0,0,0.25)',
};

const SHADOW_OPTIONS = [
  { value: 'none',   label: 'None'   },
  { value: 'light',  label: 'Light'  },
  { value: 'medium', label: 'Medium' },
  { value: 'heavy',  label: 'Heavy'  },
] as const;

// ---------------------------------------------------------------------------
// Shared label style
// ---------------------------------------------------------------------------
const lbl = 'text-sm font-medium text-foreground cursor-default w-fit';

// ---------------------------------------------------------------------------
// WidgetColorPicker — at MODULE level to prevent unmount on re-render
// ---------------------------------------------------------------------------
interface WidgetColorPickerProps {
  /** Display label shown above the inputs */
  label:   string;
  /** Tooltip description */
  tooltip: string;
  value:   string;
  onChange: (hex: string) => void;
}

/**
 * WidgetColorPicker
 *
 * Renders an inline color swatch + hex text input pair.
 * Defined at module scope so React never treats it as a new component
 * type on DashboardTab re-renders (which would close the native color picker).
 */
function WidgetColorPicker({ label, tooltip, value, onChange }: WidgetColorPickerProps) {
  return (
    <div className="flex flex-col gap-1">
      <SimpleTooltip content={tooltip} position="top">
        <label className={lbl}>{label}</label>
      </SimpleTooltip>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-10 rounded border border-input cursor-pointer p-0 overflow-hidden bg-transparent"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 px-2 py-1 text-xs bg-background border border-input rounded text-foreground font-mono"
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface DashboardTabProps {
  watchedValues: Pick<AppearanceForm, 'mode' | 'widgetDark' | 'widgetLight' | 'dashboardColumns' | 'autoRefreshInterval'>;
  register: UseFormRegister<AppearanceForm>;
  setValue: UseFormSetValue<AppearanceForm>;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * DashboardTab
 *
 * Renders the "Dashboard" tab inside the Appearance settings page.
 * Manages widget appearance state through `setValue` from react-hook-form.
 */
export function DashboardTab({ watchedValues, setValue }: DashboardTabProps) {
  const { t } = useI18n();

  // Resolve 'system' mode using the OS dark-mode media query
  const effectiveMode =
    watchedValues.mode === 'system'
      ? window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : watchedValues.mode;

  const isDarkMode = effectiveMode === 'dark';

  // Merge stored widget prefs over defaults for the current theme mode
  const currentWidget: WidgetPrefs = isDarkMode
    ? { ...DEFAULT_WIDGET_DARK, ...watchedValues.widgetDark }
    : { ...DEFAULT_WIDGET_LIGHT, ...watchedValues.widgetLight };

  /**
   * Updates a single widget preference field in the form state.
   * Always writes to the theme-appropriate key (widgetLight or widgetDark).
   */
  const handleWidgetChange = (field: keyof WidgetPrefs, value: string | number | boolean) => {
    const widgetKey = isDarkMode ? 'widgetDark' : 'widgetLight';
    const currentConfig = isDarkMode
      ? { ...DEFAULT_WIDGET_DARK, ...watchedValues.widgetDark }
      : { ...DEFAULT_WIDGET_LIGHT, ...watchedValues.widgetLight };

    setValue(widgetKey, { ...currentConfig, [field]: value }, { shouldDirty: true });
  };

  /** Returns the CSS box-shadow string for the given intensity key */
  const getShadow = (intensity: string): string =>
    SHADOW_MAP[intensity] ?? SHADOW_MAP.medium;

  // Inline style objects for the two preview widgets (avoids repetition)
  const previewWidgetStyle = {
    borderRadius: `${currentWidget.borderRadius}px`,
    opacity: currentWidget.opacity / 100,
    backgroundColor: currentWidget.bodyColor,
    borderWidth: '1px',
    borderStyle: 'solid' as const,
    borderColor: currentWidget.borderColor,
    boxShadow: getShadow(currentWidget.shadowIntensity),
  };

  return (
    <div className="space-y-6">
      <FormBox
        title={t('appearance.dashboard.widgetSettings', 'Widget Settings')}
        icon={<LayoutGrid className="h-5 w-5" />}
      >
        {/* -- Controls grid ------------------------------------------------ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">

          {/* Border Radius slider */}
          <SimpleTooltip content="Corner rounding of each dashboard widget card." position="top">
            <label className={lbl}>
              {t('appearance.dashboard.borderRadius', 'Border Radius')}
            </label>
          </SimpleTooltip>
          <div className="flex items-center gap-3">
            <input
              type="range"
              value={currentWidget.borderRadius}
              onChange={(e) => handleWidgetChange('borderRadius', parseInt(e.target.value))}
              className="flex-1"
              min="0" max="24" step="2"
            />
            <span className="text-sm text-muted-foreground w-12">{currentWidget.borderRadius}px</span>
          </div>

          {/* Opacity slider */}
          <SimpleTooltip content="Overall transparency of widget cards. 100% = fully opaque." position="top">
            <label className={lbl}>
              {t('appearance.dashboard.opacity', 'Opacity')}
            </label>
          </SimpleTooltip>
          <div className="flex items-center gap-3">
            <input
              type="range"
              value={currentWidget.opacity}
              onChange={(e) => handleWidgetChange('opacity', parseInt(e.target.value))}
              className="flex-1"
              min="50" max="100" step="5"
            />
            <span className="text-sm text-muted-foreground w-12">{currentWidget.opacity}%</span>
          </div>

          {/* Show Headers toggle */}
          <SimpleTooltip content="Toggle the title bar visibility at the top of each widget." position="top">
            <label className={lbl}>
              {t('appearance.dashboard.showHeaders', 'Show Headers')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={currentWidget.headerVisible}
              onChange={(val) => handleWidgetChange('headerVisible', val)}
            />
          </div>

          {/* Shadow select */}
          <SimpleTooltip content="Drop-shadow intensity applied to all dashboard widgets." position="top">
            <label className={lbl}>
              {t('appearance.dashboard.shadow', 'Shadow')}
            </label>
          </SimpleTooltip>
          <select
            value={currentWidget.shadowIntensity}
            onChange={(e) => handleWidgetChange('shadowIntensity', e.target.value)}
            className="px-3 py-2 text-sm bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {SHADOW_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Widget Columns */}
          <SimpleTooltip content="Number of columns in the dashboard widget grid (2, 3 or 4)." position="top">
            <label className={lbl}>
              {t('appearance.dashboard.widgetColumns', 'Widget Columns')}
            </label>
          </SimpleTooltip>
          <div className="flex gap-2">
            {([2, 3, 4] as const).map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setValue('dashboardColumns', n, { shouldDirty: true })}
                className={`w-9 h-9 rounded-lg text-sm font-medium border-2 transition-all ${
                  (watchedValues.dashboardColumns ?? 3) === n
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40'
                }`}
              >
                {n}
              </button>
            ))}
          </div>

          {/* Auto-Refresh */}
          <SimpleTooltip content="How often dashboard metrics and service statuses are refreshed automatically." position="top">
            <label className={lbl}>
              {t('appearance.dashboard.autoRefresh', 'Auto-Refresh')}
            </label>
          </SimpleTooltip>
          <select
            value={watchedValues.autoRefreshInterval ?? 30}
            onChange={(e) => setValue('autoRefreshInterval', Number(e.target.value), { shouldDirty: true })}
            className="px-3 py-2 text-sm bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {[15, 30, 60, 120, 300].map((s) => (
              <option key={s} value={s}>{s < 60 ? `${s}s` : `${s / 60}m`}</option>
            ))}
          </select>

        </div>

        {/* -- Per-theme color pickers -------------------------------------- */}
        <div className="border-t border-border pt-4 mt-4">
          <p className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
            {isDarkMode ? '\uD83C\uDF19' : '\u2600\uFE0F'}
            {isDarkMode
              ? t('appearance.dashboard.darkThemeColors',  'Dark Theme Widget Colors')
              : t('appearance.dashboard.lightThemeColors', 'Light Theme Widget Colors')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <WidgetColorPicker
              label={t('appearance.dashboard.headerColor', 'Header')}
              tooltip="Background color of the widget title bar."
              value={currentWidget.headerColor}
              onChange={(v) => handleWidgetChange('headerColor', v)}
            />
            <WidgetColorPicker
              label={t('appearance.dashboard.bodyColor', 'Body')}
              tooltip="Background color of the widget body area."
              value={currentWidget.bodyColor}
              onChange={(v) => handleWidgetChange('bodyColor', v)}
            />
            <WidgetColorPicker
              label={t('appearance.dashboard.textColor', 'Text')}
              tooltip="Primary text color used inside widgets."
              value={currentWidget.textColor}
              onChange={(v) => handleWidgetChange('textColor', v)}
            />
            <WidgetColorPicker
              label={t('appearance.dashboard.borderColor', 'Border')}
              tooltip="Border and divider color for widget elements."
              value={currentWidget.borderColor}
              onChange={(v) => handleWidgetChange('borderColor', v)}
            />
            <WidgetColorPicker
              label={t('appearance.dashboard.cardColor', 'Card')}
              tooltip="Background of inner metric cards within a widget."
              value={currentWidget.cardColor}
              onChange={(v) => handleWidgetChange('cardColor', v)}
            />
          </div>
        </div>

        {/* -- Live Preview ------------------------------------------------- */}
        <div className="border-t border-border pt-4 mt-4">
          <p className="text-sm font-medium text-muted-foreground mb-4">
            {t('appearance.dashboard.preview', 'Preview')}
          </p>
          <div className="flex gap-4">

            {/* Preview Widget 1 - Card-layout: System Status */}
            <div className="overflow-hidden flex-1" style={previewWidgetStyle}>
              {currentWidget.headerVisible && (
                <div
                  className="flex items-center justify-between px-3 py-2"
                  style={{
                    backgroundColor: currentWidget.headerColor,
                    borderBottom: `1px solid ${currentWidget.borderColor}`,
                  }}
                >
                  <span className="font-medium text-sm" style={{ color: currentWidget.textColor }}>
                    {t('appearance.dashboard.previewCardLayout', 'System Status')}
                  </span>
                  <div className="flex gap-1">
                    <div className="w-4 h-4 rounded bg-muted" />
                  </div>
                </div>
              )}
              <div className="p-3">
                <div className="flex justify-around items-center gap-2">
                  {/* Circular gauge helper - renders a mini donut-chart */}
                  {[
                    { icon: '\uD83D\uDDA5\uFE0F', label: 'CPU',    pct: 3  },
                    { icon: '\uD83D\uDCBE',        label: 'Memory', pct: 45 },
                    { icon: '\uD83D\uDCBF',        label: 'Disk',   pct: 61 },
                  ].map(({ icon, label, pct }) => (
                    <div
                      key={label}
                      className="flex flex-col items-center p-3 rounded-lg"
                      style={{ backgroundColor: currentWidget.cardColor }}
                    >
                      <div className="relative w-12 h-12">
                        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                          {/* Track circle */}
                          <circle cx="18" cy="18" r="15.5" fill="none" stroke={currentWidget.borderColor} strokeWidth="3" />
                          {/* Progress arc */}
                          <circle
                            cx="18" cy="18" r="15.5" fill="none"
                            stroke="#22c55e" strokeWidth="3"
                            strokeDasharray="97.4"
                            strokeDashoffset={97.4 - (97.4 * pct) / 100}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-green-500 text-[10px]">{icon}</span>
                          <span className="text-[10px] font-bold" style={{ color: currentWidget.textColor }}>{pct}%</span>
                        </div>
                      </div>
                      <span className="text-xs mt-1" style={{ color: currentWidget.textColor }}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Preview Widget 2 - List-layout: Services */}
            <div className="overflow-hidden flex-1" style={previewWidgetStyle}>
              {currentWidget.headerVisible && (
                <div
                  className="flex items-center justify-between px-3 py-2"
                  style={{
                    backgroundColor: currentWidget.headerColor,
                    borderBottom: `1px solid ${currentWidget.borderColor}`,
                  }}
                >
                  <span className="font-medium text-sm" style={{ color: currentWidget.textColor }}>
                    {t('appearance.dashboard.previewListLayout', 'Services')}
                  </span>
                  <div className="flex gap-1">
                    <div className="w-4 h-4 rounded bg-muted" />
                  </div>
                </div>
              )}
              <div className="p-3 space-y-1">
                {[
                  { name: 'FreeSWITCH', running: true  },
                  { name: 'PostgreSQL',  running: true  },
                  { name: 'Fail2ban',    running: false },
                ].map((svc) => (
                  <div
                    key={svc.name}
                    className="flex items-center justify-between py-1.5"
                    style={{ borderBottom: `1px solid ${currentWidget.borderColor}40` }}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${svc.running ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm" style={{ color: currentWidget.textColor }}>{svc.name}</span>
                    </div>
                    {svc.running
                      ? <span className="text-xs px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded">Running</span>
                      : <span className="text-xs px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">Stopped</span>
                    }
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </FormBox>
    </div>
  );
}
